import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Shield, Plus, X, Globe } from "lucide-react";
import { Policy, usePolicies } from "@/hooks/usePolicies";
import { useState } from "react";

interface PresetWhitelistTabProps {
  preset: Policy;
}

export const PresetWhitelistTab = ({ preset }: PresetWhitelistTabProps) => {
  const [newDomain, setNewDomain] = useState("");
  const { updatePolicy } = usePolicies();

  const handleAddDomain = async () => {
    if (newDomain.trim()) {
      try {
        const currentAllowlist = preset.allowlist || [];
        const updatedAllowlist = [...currentAllowlist, newDomain.trim()];
        await updatePolicy(preset._id, { allowlist: updatedAllowlist });
        setNewDomain("");
      } catch (error) {
        console.error('Failed to add domain to allowlist:', error);
      }
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    try {
      const currentAllowlist = preset.allowlist || [];
      const updatedAllowlist = currentAllowlist.filter(item => item !== domain);
      await updatePolicy(preset._id, { allowlist: updatedAllowlist });
    } catch (error) {
      console.error('Failed to remove domain from allowlist:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Add Domain */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>Add Allowed Domain</span>
          </CardTitle>
          <CardDescription>
            Add a new item to the allowlist for this policy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2">
            <Input
              placeholder="Enter item (e.g., example.com)"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
            />
            <Button onClick={handleAddDomain} disabled={!newDomain.trim()}>
              Add Item
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Whitelisted Domains */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-green-600" />
            <span>Allowed Items</span>
          </CardTitle>
          <CardDescription>
            Items that are explicitly allowed in this policy
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(preset.allowlist?.length || 0) === 0 ? (
            <div className="text-center py-8">
              <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No items allowed</h3>
              <p className="text-gray-500 mb-4">
                Add items that should always be accessible in this policy.
              </p>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Item
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preset.allowlist?.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <span className="font-mono text-sm">{item}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Allowed
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemoveDomain(item)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-green-900">Whitelist Information</h4>
              <p className="text-sm text-green-700 mt-1">
                Items in the allowlist are always accessible, regardless of category restrictions or other blocking rules. 
                Use this for essential services and trusted content.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};